import { Box, Button, CircularProgress, Container, Grid, Stack, Typography } from '@mui/material'
import { LargeCard } from 'Components/Cards/Large'
import FamilyIcon from '@mui/icons-material/FamilyRestroom'
import ParentIcon from '@mui/icons-material/Woman'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import StudentsPng from 'Assets/Images/students.png'
import { useEffect, useState } from 'react'
import { Modal } from 'Components/Modal'
import { sendGet } from 'hooks/use-fetch'
import { HandshakeIcon } from 'Assets/SVGs/handshake'
import S3Image from 'Components/S3Image'
import { type User, type ISDSettings, type InterestFormLabel } from 'core/types'
import { CardType, type ISDServiceDetails, type HomeState } from 'Pages/CaregiverPortal/types'
import { SimpleSelectableCard } from 'Components/Cards/CaregiverPortal/SimpleSelectableCard'
import { CustomLabelWithToolTip } from 'Components/CustomLabel/Index'

interface ISDSettingsState {
  isdName: string
  adultServicesName: string | undefined
  adultServicesLink: string | undefined
  showServicesForMyChild: boolean
  showServicesForMyself: boolean
  showCreateReferral: boolean
  isdServiceList: ISDServiceDetails[]
}

interface HomePageProps {
  state: HomeState
  isdId: string | undefined
  isdServiceProviderId: string | undefined
  user: User | null
  login: () => void
  newSession: () => void
  setState: (state: HomeState) => void
  onContinue: () => void
}

export function CaregiverPortalHomePage (props: HomePageProps): JSX.Element {
  const { state, setState } = props
  const [isExternalModalOpen, setIsExternalModalOpen] = useState(false)
  const [isdSettings, setIsdSettings] = useState<ISDSettingsState>()
  const [idkHeader, setIdkHeader] = useState('I am not sure')
  const [idkText, setIdkText] = useState('Click here if you would like someone to reach out to you after we collect some basic information about you and your child.')
  const v4Url = process.env.REACT_APP_V4URL ?? ''

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response: settings }: { response: ISDSettings } = await sendGet(`/CaregiverPortal/GetISDSettings?id=${props.isdId ?? 0}`, {})
      const { response: services } = await sendGet(`/ISDServices/GetCurrentEnabledServices?ISDID=${props.isdId ?? 0}`, {})
      const { response: headerResp }: { response: InterestFormLabel } = await sendGet(`/Label/GetDetails?isdId=${props.isdId ?? 0}&name=IF_Home_IDK_Header`, {})
      const { response: textResp }: { response: InterestFormLabel } = await sendGet(`/Label/GetDetails?isdId=${props.isdId ?? 0}&name=IF_Home_IDK_Detail`, {})
      if (headerResp != null) { setIdkHeader(headerResp.labelText) }
      if (textResp != null) { setIdkText(textResp.labelText) }
      if (settings !== null) {
        if ((settings.v4Enabled ?? false) && (props.isdId != null) && v4Url !== '') {
          let url = `${v4Url}/CaregiverPortal/${props.isdId}`
          if (props.isdServiceProviderId !== undefined) {
            url += `/${props.isdServiceProviderId}`
          }
          window.location.href = url
          return
        }
        setIsdSettings({
          isdName: settings.isdName,
          adultServicesName: settings.adultServicesName,
          adultServicesLink: settings.adultServicesLink,
          showServicesForMyChild: settings.showServicesForMyChild,
          showServicesForMyself: settings.showServicesForMyself,
          showCreateReferral: settings.showCreateReferral,
          isdServiceList: services ?? []
        })
      }
    }

    void fetchData()
  }, [props.isdId])

  const handleSelectCard = (card: CardType): void => {
    if (state.selectedCard !== card) {
      setState({ selectedCard: card, unbornChild: null, selectedServices: undefined })
    }
  }

  const handleSelectIsUnborn = (isUnborn: boolean): void => {
    setState({ ...state, unbornChild: isUnborn, selectedServices: undefined })
  }

  const handleSelectISDService = (service?: ISDServiceDetails): void => {
    const selectedServices = state.selectedServices ?? []

    if (service === undefined) {
      setState({ ...state, selectedServices: [] })
    } else {
      const index = selectedServices.findIndex(x => x.id === service.id)

      if (index !== -1) {
        selectedServices.splice(index, 1)
      } else {
        selectedServices.push(service)
      }

      setState({ ...state, selectedServices })
    }
  }

  const handleContinue = async (): Promise<void> => {
    const invalid = state.selectedCard === null || state.unbornChild === null || (state.selectedCard === CardType.MY_CHILD && state.selectedServices === undefined)
    if (!invalid) {
      props.onContinue()
    }
  }

  const handleCloseExternalModal = (): void => {
    setIsExternalModalOpen(false)
    setState({ selectedCard: null, unbornChild: null, selectedServices: undefined })
  }

  const getIsdServiceList = (): ISDServiceDetails[] => {
    if (state.unbornChild === true) {
      return isdSettings?.isdServiceList.filter(x => !x.hasAgeRange) ?? []
    } else {
      return isdSettings?.isdServiceList ?? []
    }
  }

  const getIcon = (s3Key: string | null): JSX.Element => {
    if (s3Key !== null) {
      return <S3Image
        imageKey={s3Key}
        alt='logo'
        className='service-card-icon'
      />
    } else { return <></> }
  }

  const unbornChildSection = <>
    <Grid item xs={12}>
      <Typography variant='h6' data-testid='unborn-child-title'>Is this an unborn child?</Typography>
    </Grid>
    <Grid container item spacing={2}>
      <Grid item xs={6}>
        <Button variant='outlined' className={'wide-button' + (state.unbornChild === true ? ' selected' : '')} data-testid='handle-unborn-yes' onClick={() => { handleSelectIsUnborn(true) }}>Yes</Button>
      </Grid>
      <Grid item xs={6}>
        <Button variant='outlined' className={'wide-button' + (state.unbornChild === false ? ' selected' : '')} data-testid='handle-unborn-no' onClick={() => { handleSelectIsUnborn(false) }}>No</Button>
      </Grid>
    </Grid>
  </>

  const isdServiceListSection = <>
    <Grid item xs={12}>
      <Typography variant='h6' data-testid='service-selection-title'>What services are you interested in?</Typography>
    </Grid>
    <Grid item xs={12}>
      <Stack
        data-testid='service-selection-stack'
        direction='column'
        spacing={2}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'stretch'
        }}
      >
        {getIsdServiceList().map((service) => <SimpleSelectableCard
          key={service.id}
          header={service.name}
          description={service.description}
          imageElement={getIcon(service.s3Key)}
          selected={state.selectedServices?.find(x => x.id === service.id) !== undefined}
          onClick={() => { handleSelectISDService(service) }}
        />)}
        <SimpleSelectableCard
          header={idkHeader}
          description={idkText}
          imageElement={<QuestionMarkIcon className='service-card-icon' />}
          selected={state.selectedServices?.length === 0}
          onClick={() => { handleSelectISDService() }}
        />
      </Stack>
    </Grid>
  </>

  const continueButton = <Grid item xs={12} sx={{ mt: 1 }}>
    <Button
      data-testid='continue-button'
      className='footer-button'
      onClick={async () => { await handleContinue() }}
      variant='contained'
    >
      Continue
    </Button>
  </Grid>

  if (isdSettings === undefined) { return <CircularProgress /> }

  return <>
    <img src={StudentsPng} alt='students' className='students-image' />
    <Container>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ my: 1 }}>
          <Typography variant='h4'>Welcome to Michigan Early Childhood Connect!</Typography>
          <Typography variant='body1'>Welcome to the MiECC Caregiver Portal for {isdSettings.isdName}! Before we get started, please provide us with a few key pieces of information.</Typography>
        </Grid>

        {!isdSettings.showServicesForMyChild && !isdSettings.showServicesForMyself && !isdSettings.showCreateReferral && <Grid item xs={12}>
          <Typography variant='h5' sx={{ textAlign: 'center' }}>
            This ISD does not currently have any services available. Please contact your ISD for a proper link.
          </Typography>
        </Grid>}

        {isdSettings.showServicesForMyChild && <>
          <Grid item xs={12}>
            <LargeCard
              fullWidth={true}
              data-testid='services-for-my-child-card'
              className={'services-option-card' + (state.selectedCard === CardType.MY_CHILD ? ' selected' : '')}
              onClick={() => { handleSelectCard(CardType.MY_CHILD) }}
              content={
                <Box className='d-flex'>
                  <FamilyIcon className='card-icon' />
                  <Box sx={{ marginLeft: '10px', marginRight: '15px' }}>
                    <Typography variant='h5' className='card-subheader'>Services for My Child</Typography>
                    <Box sx={{ marginTop: '10px' }}>
                      <CustomLabelWithToolTip
                        name='IF_Home_Child_Services'
                        isdId={props.isdId ?? 0}
                        defaultLabelText='We will guide you through a process that will put you in touch with the services and programs that best meet the needs of your child (preschool, childcare, basic needs, home visiting, etc.).'
                        sx={{ cursor: 'pointer' }}
                      />
                    </Box>
                  </Box>
                </Box>
              }
            />
          </Grid>
          <Grid container item spacing={2} sx={{ ml: 1 }}>
            {state.selectedCard === CardType.MY_CHILD && unbornChildSection}
            {state.selectedCard === CardType.MY_CHILD && state.unbornChild !== null && isdSettings?.isdServiceList?.length > 0 && <>
              {isdServiceListSection}
              {state.selectedServices !== undefined && continueButton}
            </>}
          </Grid>
        </>}

        {isdSettings.showServicesForMyself && isdSettings.adultServicesLink != null && isdSettings.adultServicesLink !== '' && <Grid item xs={12}>
          <LargeCard
            fullWidth={true}
            data-testid='services-for-myself-card'
            className={'services-option-card' + (state.selectedCard === CardType.MYSELF ? ' selected' : '')}
            onClick={() => {
              handleSelectCard(CardType.MYSELF)
              setIsExternalModalOpen(true)
            }}
            content={
              <Box className='d-flex'>
                <ParentIcon className='card-icon' />
                <Box sx={{ marginLeft: '10px', marginRight: '15px' }}>
                  <Typography variant='h5' className='card-subheader'>Services for Myself</Typography>
                  <Box sx={{ marginTop: '10px' }}>
                    <Typography variant='body2'>We will take you to some external resources.</Typography>
                  </Box>
                </Box>
              </Box>
            }
          />
        </Grid>}

        {isdSettings.showCreateReferral && <>
          <Grid item xs={12}>
            <LargeCard
              fullWidth={true}
              data-testid='create-referral-card'
              className={'services-option-card' + (state.selectedCard === CardType.OTHER_CHILD ? ' selected' : '')}
              onClick={() => { handleSelectCard(CardType.OTHER_CHILD) }}
              content={
                <Box className='d-flex'>
                  <HandshakeIcon className='card-icon' fill='rgb(114, 42, 130)' />
                  <Box sx={{ marginLeft: '10px', marginRight: '15px' }}>
                    <Typography variant='h5' className='card-subheader'>Create a Referral</Typography>
                    <Box sx={{ marginTop: '10px' }}>
                      <CustomLabelWithToolTip
                        name='IF_Home_Create_Referral'
                        isdId={props.isdId ?? 0}
                        defaultLabelText='If you are a service provider who would like to create a referral on behalf of a family, click here to get started.'
                        sx={{ cursor: 'pointer' }}
                      />
                    </Box>
                  </Box>
                </Box>
              }
            />
          </Grid>
          <Grid container item spacing={2} sx={{ ml: 1 }}>
            {state.selectedCard === CardType.OTHER_CHILD && <>
              {unbornChildSection}
              {state.unbornChild !== null && continueButton}
            </>}
          </Grid>
        </>}

        {props.user === null && <Grid item xs={12}>
          <Typography variant='h5' sx={{ textAlign: 'center', marginTop: '30px' }}>Returning user?</Typography>
          <Button
            className='footer-button'
            onClick={props.login}
            variant='dashed'
            autoCapitalize='false'
          >
            Login
          </Button>
        </Grid>}
      </Grid>
    </Container>

    <Modal
      onClose={handleCloseExternalModal}
      open={isExternalModalOpen}
      title={'External Redirect'}
      confirmationContent={<Box>This action will take you away from MiECC and to {isdSettings.adultServicesName}. Do you wish to continue?</Box>}
      confirmButtonText={'Yes, Continue'}
      cancelButtonText='No, Stay Here'
      maxWidth={'sm'}
      data-testid='external-services-modal'
      onConfirm={() => { window.location.href = isdSettings.adultServicesLink ?? '' }}
    />
  </>
}
